import { z } from 'zod';

// ---------- Forms ----------

export const FORM_FIELD_TYPES = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'number',
  'date',
  'dropdown',
  'multi_select',
  'checkbox',
  'rating',
  'file',
  'heading',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/** What a field feeds on the task that a submission creates. */
export const FORM_MAPPINGS = ['none', 'task_name', 'description', 'due_date', 'start_date', 'priority', 'assignee', 'tags'] as const;
export type FormMapping = (typeof FORM_MAPPINGS)[number] | `custom_field:${string}`;

export const CONDITION_OPS = ['is', 'is_not', 'contains', 'is_set', 'is_not_set'] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];

/** Show a field only when another field's answer matches. */
export interface FieldCondition {
  fieldId: string;
  op: ConditionOp;
  value?: string;
}

export interface FormFieldConfig {
  placeholder?: string;
  /** dropdown, multi_select */
  options?: string[];
  /** rating: number of stars (default 5) */
  max?: number;
  /** file: accepted mime patterns, e.g. ["image/*"] */
  accept?: string[];
  condition?: FieldCondition;
}

export interface FormBranding {
  logo?: string | null;
  /** Accent colour for the public page. */
  color?: string | null;
  /** Cover: CSS gradient or an image URL. */
  cover?: string | null;
}

export interface FormSettings {
  thankYou?: string;
  redirectUrl?: string | null;
  submitLabel?: string;
  branding?: FormBranding;
  /** Allow more than one submission per browser session (default true). */
  allowMultiple?: boolean;
}

export interface FormFieldDTO {
  id: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  helpText: string | null;
  mapsTo: FormMapping;
  config: FormFieldConfig;
  position: string;
}

export interface FormDTO {
  id: string;
  listId: string;
  title: string;
  description: string | null;
  slug: string;
  isPublic: boolean;
  settings: FormSettings;
  submissionCount: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  fields: FormFieldDTO[];
}

/** What the public page gets: no ids of people, no list. */
export interface PublicFormDTO {
  title: string;
  description: string | null;
  settings: Pick<FormSettings, 'thankYou' | 'redirectUrl' | 'submitLabel' | 'branding' | 'allowMultiple'>;
  fields: FormFieldDTO[];
  /** Options for assignee fields are shown by name only. */
  assignees: { id: string; name: string }[];
  tags: { id: string; name: string; color: string }[];
}

export interface FormSubmissionDTO {
  id: string;
  formId: string;
  taskId: string | null;
  taskName: string | null;
  data: Record<string, unknown>;
  createdAt: number;
}

export interface FormChangedEventData {
  formId: string;
  op: 'created' | 'updated' | 'deleted' | 'submitted';
}

const id = z.string().min(1).max(64);
const slugSchema = z
  .string()
  .trim()
  .min(3)
  .max(60)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and dashes');

const fieldConditionSchema = z.object({ fieldId: id, op: z.enum(CONDITION_OPS), value: z.string().max(500).optional() });

export const formFieldSchema = z.object({
  /** Keep ids stable between saves so answers and conditions stay attached. */
  id: id.optional(),
  label: z.string().trim().min(1).max(200),
  type: z.enum(FORM_FIELD_TYPES),
  required: z.boolean().optional(),
  helpText: z.string().max(500).nullable().optional(),
  mapsTo: z.string().max(80).optional(),
  config: z
    .object({
      placeholder: z.string().max(200).optional(),
      options: z.array(z.string().max(120)).max(100).optional(),
      max: z.number().int().min(1).max(10).optional(),
      accept: z.array(z.string().max(60)).max(20).optional(),
      condition: fieldConditionSchema.optional(),
    })
    .optional(),
});
export type FormFieldInput = z.infer<typeof formFieldSchema>;

const brandingSchema = z.object({
  logo: z.string().max(300).nullable().optional(),
  color: z.string().max(32).nullable().optional(),
  cover: z.string().max(300).nullable().optional(),
});

export const formSettingsSchema = z.object({
  thankYou: z.string().max(1000).optional(),
  redirectUrl: z.string().url().max(500).nullable().optional(),
  submitLabel: z.string().trim().max(40).optional(),
  branding: brandingSchema.optional(),
  allowMultiple: z.boolean().optional(),
});

export const createFormSchema = z.object({
  listId: id,
  title: z.string().trim().min(1).max(200),
});
export type CreateFormInput = z.infer<typeof createFormSchema>;

export const updateFormSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  slug: slugSchema.optional(),
  isPublic: z.boolean().optional(),
  listId: id.optional(),
  settings: formSettingsSchema.optional(),
  /** Replaces the whole field list, in order. */
  fields: z.array(formFieldSchema).max(60).optional(),
});
export type UpdateFormInput = z.infer<typeof updateFormSchema>;

/** A public submission: answers by field id, plus the honeypot that must stay empty. */
export const submitFormSchema = z.object({
  answers: z.record(z.string(), z.unknown()),
  /** Spam trap: bots fill every input. */
  website: z.string().max(200).optional(),
  /** Attachment ids from the public upload endpoint, by field id. */
  files: z.record(z.string(), z.array(id).max(10)).optional(),
});
export type SubmitFormInput = z.infer<typeof submitFormSchema>;

/** Whether a field is shown given the current answers (conditional logic). */
export function fieldVisible(field: Pick<FormFieldDTO, 'config'>, answers: Record<string, unknown>): boolean {
  const c = field.config.condition;
  if (!c) return true;
  const raw = answers[c.fieldId];
  const value = Array.isArray(raw) ? raw.map(String) : raw === undefined || raw === null ? [] : [String(raw)];
  const has = value.some((v) => v !== '' && v !== 'false');
  switch (c.op) {
    case 'is_set':
      return has;
    case 'is_not_set':
      return !has;
    case 'is':
      return value.some((v) => v.toLowerCase() === (c.value ?? '').toLowerCase());
    case 'is_not':
      return !value.some((v) => v.toLowerCase() === (c.value ?? '').toLowerCase());
    case 'contains':
      return value.some((v) => v.toLowerCase().includes((c.value ?? '').toLowerCase()));
  }
}

/** The answers a submission must have: required fields that are visible and not headings. */
export function missingAnswers(fields: FormFieldDTO[], answers: Record<string, unknown>, files: Record<string, string[]> = {}): FormFieldDTO[] {
  return fields.filter((f) => {
    if (!f.required || f.type === 'heading' || !fieldVisible(f, answers)) return false;
    if (f.type === 'file') return (files[f.id]?.length ?? 0) === 0;
    const v = answers[f.id];
    if (Array.isArray(v)) return v.length === 0;
    if (f.type === 'checkbox') return v !== true;
    return v === undefined || v === null || String(v).trim() === '';
  });
}

/** A URL friendly slug from a title, with a short random tail so two forms never collide. */
export function slugify(title: string, tail: string): string {
  const base = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `${base || 'form'}-${tail}`;
}

/** A date-only answer ("2026-10-14", or any parseable date) as UTC midnight, or null. */
export function coerceDateOnly(text: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text.trim());
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}
